import {
  Alert,
  AlertIcon,
  Button,
  ButtonGroup,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  VStack,
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from 'urql';
import {
  faChevronDown,
  faCube,
  faFile,
  faTable,
} from '@fortawesome/free-solid-svg-icons';
import { useFlag } from '@unleash/proxy-client-react';
import { useExportState } from '../common/ExportStateManagerContext';
import {
  ExportShapesToIfcDocument,
  ExportShapesToIfcStatusDocument,
} from 'src/gql/graphql';
import { useUserTenant } from 'src/services/auth-info';
import SparkelIcon from 'src/components/common/icon/SparkelIcon';

export default function ExportProjectTab(): React.ReactElement {
  const { t } = useTranslation('company');
  const { triggerExport, isSuccessful, isExporting } = useExportState();
  const { projectId } = useParams<{ projectId: string }>();
  const { tenant } = useUserTenant();

  const enableExportShapesToIfc = useFlag('enable-export-shapes-to-ifc');

  const [isExportingShapes, setIsExportingShapes] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  type ExportShapesResult = {
    exportShapesToIfc: {
      success: boolean;
      message?: string | null;
      jobId?: string | null;
    };
  };
  type ExportShapesVars = {
    input: {
      tenant: string;
      projectId: string;
      includeClassifications?: boolean;
    };
  };
  const [, exportShapesToIfc] = useMutation<
    ExportShapesResult,
    ExportShapesVars
  >(ExportShapesToIfcDocument);

  const [{ data: statusData }, reexecuteStatusQuery] = useQuery({
    query: ExportShapesToIfcStatusDocument,
    variables: { projectId: projectId as string, jobId: jobId as string },
    pause: !projectId || !jobId,
    requestPolicy: 'network-only',
  });

  const handleExportProject = () => {
    triggerExport();
  };

  const handleExportShapesToIfc = useCallback(
    async (includeClassifications = false) => {
      if (!projectId) return;
      setIsExportingShapes(true);
      try {
        if (!tenant?.group) return;
        setDownloadUrl(null);
        const result = await exportShapesToIfc({
          input: {
            tenant: tenant.group,
            projectId,
            includeClassifications,
          },
        });
        const payload = result.data?.exportShapesToIfc;
        // eslint-disable-next-line no-console
        console.log(
          `status: ${payload?.success ? 'success' : 'error'}, message: ${
            payload?.message
          }`
        );
        if (payload?.jobId) setJobId(payload.jobId);
      } finally {
        setIsExportingShapes(false);
      }
    },
    [exportShapesToIfc, projectId, tenant]
  );

  useEffect(() => {
    if (!projectId || !jobId) return;

    const intervalId = window.setInterval(() => {
      reexecuteStatusQuery({ requestPolicy: 'network-only' });
    }, 3000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [projectId, jobId, reexecuteStatusQuery]);

  useEffect(() => {
    const status = statusData?.exportShapesToIfcStatus?.status;
    const url = statusData?.exportShapesToIfcStatus?.downloadUrl;
    if (status === 'COMPLETED' && url) {
      setDownloadUrl(url);
    }
  }, [statusData]);

  return (
    <VStack spacing={2} alignItems={'start'}>
      <Alert status="info" borderRadius={'lg'}>
        <AlertIcon />
        {t('project-settings.export.info')}
      </Alert>
      {isSuccessful || !!downloadUrl ? (
        <Alert status="success" borderRadius={'lg'}>
          <AlertIcon />
          <HStack spacing={4}>
            <span>{t('project-settings.export.success')}</span>
            {downloadUrl ? (
              <Button
                as="a"
                href={downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="link"
                leftIcon={<SparkelIcon icon={faFile} color={'inherit'} />}
              >
                {t('project-settings.export.download-ifc')}
              </Button>
            ) : undefined}
          </HStack>
        </Alert>
      ) : undefined}
      <HStack alignSelf={'end'} spacing={4}>
        {enableExportShapesToIfc && (
          <ButtonGroup isAttached variant="outline">
            <Button
              onClick={() => handleExportShapesToIfc(false)}
              isLoading={isExportingShapes || (!!jobId && !downloadUrl)}
              borderRadius={'md'}
              leftIcon={<SparkelIcon icon={faCube} color={'inherit'} />}
            >
              {t('project-settings.export.export-shapes-to-ifc')}
            </Button>
            <Menu>
              <MenuButton
                as={IconButton}
                icon={<SparkelIcon icon={faChevronDown} color={'inherit'} />}
                isDisabled={isExportingShapes || (!!jobId && !downloadUrl)}
                borderRadius={'md'}
                aria-label={t(
                  'project-settings.export.export-shapes-options-label'
                )}
              />
              <MenuList>
                <MenuItem onClick={() => handleExportShapesToIfc(true)}>
                  {t(
                    'project-settings.export.export-with-links-as-classifications'
                  )}
                </MenuItem>
              </MenuList>
            </Menu>
          </ButtonGroup>
        )}
        <Button
          onClick={handleExportProject}
          isLoading={isExporting}
          borderRadius={'md'}
          variant={'outline'}
          leftIcon={<SparkelIcon icon={faTable} color={'inherit'} />}
        >
          {t('project-settings.export.start-export')}
        </Button>
      </HStack>
    </VStack>
  );
}
